import React, { useState, useCallback, useEffect } from 'react';
import { Banner } from '../../../common/Banner';
import { PageLoader } from '../../../loader';
import { PeriodSelector } from '../../../common/PeriodsSelector';
import { useAvailableYears } from '../../../../hooks/useAvailableYears';
import { usePeriodLogic } from '../../../../hooks/usePeriodLogic';
import MonthlyDetails from './MonthyReportsAux/MonthlyDetails';
import { getSocialQuotasSendConfirmed } from '../../../../services/quotasService';

const MonthlyReports = () => {
  const [formState, setFormState] = useState({
    selectedYear: '',
    reportType: '',
    selectedPeriod: ''
  });
  const [response, setResponse] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const { availableYears, isLoading, error: yearsError } = useAvailableYears();
  const { getApiPeriodName } = usePeriodLogic();

  // Manejar cambios del selector
  const handleFormChange = useCallback((field, value) => {
    setFormState(prev => {
      const newState = { ...prev, [field]: value };
      
      if (field === 'selectedYear') {
        newState.reportType = '';
        newState.selectedPeriod = '';
      } else if (field === 'reportType') {
        newState.selectedPeriod = '';
      }
      
      return newState;
    });
    setResponse(null);
    setError(null); // Limpiar error cuando cambia el formulario
  }, []);

  const loadData = useCallback(async () => {
    const { selectedYear, reportType, selectedPeriod } = formState;
    if (selectedYear && reportType && selectedPeriod) {
      setLoadingData(true);
      setError(null);
      setResponse(null); // Limpiar respuesta anterior
      const periodName = getApiPeriodName(reportType, selectedPeriod);
      try {
        const response = await getSocialQuotasSendConfirmed(selectedYear, periodName);
        
        // Verificar si la respuesta contiene un error
        if (response && (response.statusCode >= 400 || response.error)) {
          setResponse(null);
          setError("Error al obtener las cuotas sociales. Por favor, intente nuevamente más tarde.");
        } else {
          setResponse(response);
        }
      } catch (error) {
        console.error("Error fetching social quotas:", error);
        setResponse(null);
        setError("Error al obtener las cuotas sociales. Por favor, intente nuevamente más tarde.");
      } finally {
        setLoadingData(false);
      }
    } else {
      setResponse(null);
      setError(null); // Limpiar error si el formulario no está completo
      setLoadingData(false);
    }
  }, [formState, getApiPeriodName]);

  // Simular carga de datos cuando el formulario está completo
  useEffect(() => {
    const { selectedYear, reportType, selectedPeriod } = formState;
    
    if (selectedYear && reportType && selectedPeriod) {
      loadData();     
    }
  }, [formState, loadData]); // Cambié getApiPeriodName por loadData

  // Manejar error de años disponibles
  if (isLoading) {
    return <PageLoader message="Cargando reportes..." />;
  }

  if (yearsError) {
    return (
      <div className="w-full space-y-6">
        <Banner title="Error" message={yearsError} />
      </div>
    );
  }

  const isFormComplete = formState.selectedYear && formState.reportType && formState.selectedPeriod;

  return (
    <div className="w-full space-y-6">
      <Banner
        title="Reportes mensuales de las cuotas"
        message="Aquí puedes ver los reportes mensuales relacionados con las cuotas."
      />

      <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
        <PeriodSelector
          availableYears={availableYears}
          selectedYear={formState.selectedYear}
          reportType={formState.reportType}
          selectedPeriod={formState.selectedPeriod}
          onYearChange={(year) => handleFormChange('selectedYear', year)}
          onReportTypeChange={(type) => handleFormChange('reportType', type)}
          onPeriodChange={(period) => handleFormChange('selectedPeriod', period)}
          disabled={loadingData}
        />

        {loadingData && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-blue-800">Cargando datos...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <span className="text-red-600">{error}</span>
            </div>
            <div className="mt-2">
              <span className="text-sm text-red-800">Recuerde que para ver los reportes mensuales las cuotas debieron haber sido confirmadas y enviadas mediante el proceso correspondiente.</span>
            </div>
          </div>
        )}

        {/* Condición mejorada para renderizar MonthlyDetails */}
        {isFormComplete && !loadingData && !error && response && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <MonthlyDetails response={response} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyReports;